import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { GetRowIdParams, IServerSideDatasource, ValueGetterParams } from 'ag-grid-enterprise';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { getCellRenderer, getValueGetter } from 'src/app/modules/core/components/ag-grid';
import { DataTableServerComponent } from 'src/app/modules/core/components/data-table-server/data-table.component';
import { GridHelperService } from 'src/app/modules/core/services/grid-helper.service';
import { PicklistService } from 'src/app/modules/core/services/picklist/picklist.service';
import {
  DocumentReader,
  FieldDataType,
  QueryParams,
  SchemaField,
  SystemFieldType,
  Workflow,
  WorkflowParentType,
  WorkflowStepTypes
} from 'src/app/modules/core/store/models';
import * as fromRouting from 'src/app/modules/core/store/routing';
import * as fromComm from 'src/app/modules/communications/store/communications';
import {
  MassNotification,
  MassNotificationItemSource,
  MassNotificationItemState
} from 'src/app/modules/communications/store/communications/communications.model';
import { CommunicationsApiService } from 'src/app/modules/communications/store/communications/communications-api.service';
import { v4 as uuidv4 } from 'uuid';
import { WorkflowApiService } from '../../../store/workflow/workflow-api.service';
import * as fromAuth from 'src/app/modules/authentication/store/auth/auth.selectors';
import { UserContext, Permissions } from '@app/modules/authentication/store/auth';
import { OrgBusinessUnitsApiService } from '@app/modules/core/org-business-units/org-business-units-api.service';
import { UserContextService } from '@app/modules/core/services/user-context.service';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Component({
  selector: 'topo-comms-notification-list',
  templateUrl: './notification-list.component.html',
  styleUrls: ['./notification-list.component.scss'],
  standalone: false
})
export class NotificationListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('table') dataTable: DataTableServerComponent;

  notificationToDelete: MassNotification;
  destroy$ = new Subject<void>();
  datasource: any;
  columDefs: any[] = [];
  skip = 0;
  take = 10;
  totalCount: number = 0;
  workflows: Workflow[];
  notificationCounts: any[];
  notificationRuleDetails: any[];
  logNotification: MassNotification;
  isTableLoading: boolean = true;
  hasBusinessUnits: boolean = true;
  canAddNotification: boolean;
  userContext: UserContext;
  Permissions = Permissions;

  selectedSourceType: MassNotificationItemSource = MassNotificationItemSource.Manual;
  sourceTypes = [
    {
      value: MassNotificationItemSource.Manual,
      name: this.translateService.instant(`COMMUNICATIONS.SOURCE-TYPE-${MassNotificationItemSource.Manual}`)
    },
    {
      value: MassNotificationItemSource.WorkflowRule,
      name: this.translateService.instant(`COMMUNICATIONS.SOURCE-TYPE-${MassNotificationItemSource.WorkflowRule}`)
    }
  ];

  protected userContextService = inject(UserContextService);
  private notify = inject(SystemNotificationsService);

  constructor(
    private store: Store<any>,
    private translateService: TranslateService,
    private gridHelper: GridHelperService,
    private picklistService: PicklistService,
    private commApiService: CommunicationsApiService,
    private workflowApiService: WorkflowApiService,
    private orgBusinessUnitsApiService: OrgBusinessUnitsApiService
  ) {}

  ngOnInit(): void {
    if (!this.userContextService.canReadCommunicationNotification()) {
      this.notify.error(this.translateService.instant('COMMON.MISSING-PERMISSIONS'));
      return;
    }

    this.store
      .select(fromAuth.selectUserContext)
      .pipe(takeUntil(this.destroy$))
      .subscribe(userContext => {
        if (!!userContext) {
          this.userContext = userContext;
          this.canAddNotification = !!!this.hasBusinessUnits || !!this.userContext?.businessUnitUId;
        }
      });

    this.store
      .select(fromAuth.selectPermissionsChangeTime)
      .pipe(takeUntil(this.destroy$))
      .subscribe(time => {
        if (!!time) {
          this.columDefs = this.generateColumns();
          this.dataTable?.refreshGrid();
        }
      });

    this.orgBusinessUnitsApiService.hasOrgBusinessUnits().subscribe(hasBusinessUnits => {
      this.hasBusinessUnits = hasBusinessUnits;
      this.canAddNotification = !!!this.hasBusinessUnits || !!this.userContext?.businessUnitUId;
    });

    this.datasource = this.getServerSideDatasource();

    this.loadWorkflowTemplates();

    this.columDefs = this.generateColumns();

    this.store
      .select(fromComm.selectIsdMassNotificationsTableChanged)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isChanged => {
        if (isChanged === true) {
          this.dataTable?.refreshGrid();
        }
      });

    // called when signalR updates the stats
    this.store
      .select(fromComm.selectSelectedMassNotificationStats)
      .pipe(takeUntil(this.destroy$))
      .subscribe(stats => {
        if (!!stats) {
          const rowNode = this.dataTable?.gridApi?.getRowNode(stats.notification?.uId);
          if (!!rowNode) {
            const data = {
              ...stats.notification,
              recipientsCount: null
            };
            rowNode.setData(data);
            this.getNotificationRecipientsCount([data]);
            this.getNotificationRuleDetails([data]);
          }
        }
      });
  }

  ngAfterViewInit() {
    this.dataTable?.groupRows(false);
  }

  getServerSideDatasource(): IServerSideDatasource {
    this.isTableLoading = true;
    return {
      getRows: params => {
        const translatedParams = this.gridHelper.gridParamsToRequestParams(params.request, this.getFilterClause());

        let sortField = 'UpdatedOn';
        let sortDirection = 'Descending';
        if (params?.request.sortModel?.length > 0) {
          sortField = params?.request.sortModel[0].colId;
          sortDirection = params?.request.sortModel[0].sort === 'asc' ? 'Ascending' : 'Descending';
        }

        const query = {
          skip: this.skip,
          take: this.take,
          sortField: sortField,
          sortDirection: sortDirection,
          where: translatedParams.where,
          isConfigsExcluded: true
        };
        this.commApiService.getMassNotifications(query).subscribe(
          resp => {
            const response = this.gridHelper.queryResultToGridData(resp);
            this.totalCount = response?.rowCount;

            this.store.dispatch(fromComm.setMassNotificationsTableChanged({ changed: false }));

            const rowData = response?.rowData?.map(d => {
              const data = {
                ...d,
                recipientsCount: null
              };
              return data;
            });

            params.success({
              rowData: rowData,
              rowCount: response?.rowCount
            });

            this.getNotificationRecipientsCount(rowData);
            this.getNotificationRuleDetails(rowData);
          },
          err => params.fail()
        );
      }
    };
  }

  private getFilterClause(): string {
    if (this.selectedSourceType === MassNotificationItemSource.Manual) {
      return `Source!=${MassNotificationItemSource.WorkflowRule}`;
    } else {
      return `Source=${this.selectedSourceType} && State=${MassNotificationItemState.Ready}`;
    }
  }

  private getNotificationRecipientsCount(notifications: any[]): void {
    const uids = notifications?.map(n => n.uId);

    this.commApiService.getMassNotificationRecipientCounts(uids).subscribe(resp => {
      this.notificationCounts = [];
      resp?.forEach(r => {
        const notification = notifications.find(n => n.uId === r.massNotificationUId);
        if (!!notification) {
          this.notificationCounts.push({
            uId: notification.uId,
            count: r.count
          });
          notification.recipientsCount = r.count;
          const rowNode = this.dataTable?.gridApi?.getRowNode(notification.uId);
          if (!!rowNode) {
            rowNode.setDataValue('recipientsCount', r.count);
          }
        }
      });
    });
  }

  private getNotificationRuleDetails(notifications: any[]): void {
    if (this.selectedSourceType != MassNotificationItemSource.WorkflowRule) {
      return;
    }
    this.notificationRuleDetails = [];
    // check for notifications that are missing the source info
    let uids = [];
    notifications?.forEach(n => {
      if (!!!n.workflowState?.ruleDatasetName || !!!n.workflowState?.ruleStepName) {
        uids.push(n.uId);
      } else {
        this.notificationRuleDetails.push({
          uId: n.uId,
          ruleDatasetName: n.workflowState?.ruleDatasetName,
          ruleStepName: n.workflowState?.ruleStepName
        });
        const rowNode = this.dataTable?.gridApi?.getRowNode(n.uId);
        if (!!rowNode) {
          rowNode.setDataValue('_sourceDataset', n.workflowState?.ruleDatasetName);
          rowNode.setDataValue('_sourceStep', n.workflowState?.ruleStepName);
        }
      }
    });

    if (uids.length == 0) {
      return;
    }

    this.commApiService.getMassNotificationRuleDetails(uids).subscribe(resp => {
      resp.forEach(r => {
        const notification = notifications.find(n => n.uId === r.massNotificationUId);
        if (!!notification) {
          this.notificationRuleDetails.push({
            uId: notification.uId,
            ruleDatasetName: r.ruleDatasetName,
            ruleStepName: r.ruleStepName
          });
          if (!!!notification.workflowState) {
            notification.workflowState = {};
          }
          notification.workflowState.ruleDatasetName = r.ruleDatasetName;
          notification.workflowState.ruleStepName = r.ruleStepName;
          const rowNode = this.dataTable?.gridApi?.getRowNode(notification.uId);
          if (!!rowNode) {
            rowNode.setDataValue('_sourceDataset', r.ruleDatasetName);
            rowNode.setDataValue('_sourceStep', r.ruleStepName);
          }
        }
      });
    });
  }

  sourceTypeChanged(event: any): void {
    this.skip = 0;
    this.columDefs = this.generateColumns();
    this.dataTable?.refreshGrid();
  }

  onPaginate(skip: number): void {
    this.skip = skip;
  }

  onTakeChanged(take: number): void {
    this.take = take;
  }

  private generateColumns() {
    var columns = [];

    if (
      this.userContext?.hasPermission(Permissions.CommunicationNotificationUpdate) ||
      this.userContext?.hasPermission(Permissions.CommunicationNotificationDelete) ||
      (this.userContext?.hasPermission(Permissions.DocumentRead) &&
        this.userContext?.hasPermission(Permissions.CommunicationNotificationRead))
    ) {
      columns.push({
        colId: '_actions',
        headerName: '',
        field: '',
        floatingFilter: false,
        sortable: false,
        resizable: false,
        minWidth: 64,
        suppressHeaderMenuButton: true,
        cellRenderer: 'actionCellComponent',
        cellRendererParams: {
          location: 'right',
          getActions: this.getNotificationActions.bind(this)
        },
        width: 64,
        maxWidth: 64
      });
    }

    const nameField = {
      dataType: FieldDataType.Text,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.NAME'),
      systemName: 'name',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    var valueGetter = getValueGetter(FieldDataType.Text);
    var cellRenderer = this.userContext?.hasPermission(Permissions.CommunicationNotificationUpdate)
      ? 'linkCellComponent'
      : getCellRenderer(FieldDataType.Text);
    columns.push({
      headerName: nameField.displayName,
      field: nameField.systemName,
      colId: 'name',
      sortable: true,
      resizable: true,
      width: 400,
      menuTabs: [],
      cellRenderer,
      cellRendererParams: {
        schemaField: nameField,
        picklistService: this.picklistService,
        clicked: data => {
          this.editClick(data);
        }
      },
      valueGetter
    });

    if (this.selectedSourceType === MassNotificationItemSource.WorkflowRule) {
      const sourceDataset = {
        dataType: FieldDataType.Text,
        displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.SOURCE-DATASET'),
        systemName: 'sourceDataset',
        uId: uuidv4(),
        isUniqueKey: false,
        fieldType: SystemFieldType.System,
        picklistValues: [],
        sourceUnit: null,
        settings: null
      } as SchemaField;

      const ruleValueGetter = this.getRuleDatasetNameRenderer;
      cellRenderer = getCellRenderer(FieldDataType.Text);
      columns.push({
        headerName: sourceDataset.displayName,
        field: sourceDataset.systemName,
        colId: '_sourceDataset',
        sortable: false,
        resizable: true,
        width: 150,
        menuTabs: [],
        cellRenderer,
        cellRendererParams: {
          schemaField: sourceDataset,
          picklistService: this.picklistService
        },
        ruleValueGetter
      });

      const sourceStep = {
        dataType: FieldDataType.Text,
        displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.SOURCE-STEP'),
        systemName: 'sourceStep',
        uId: uuidv4(),
        isUniqueKey: false,
        fieldType: SystemFieldType.System,
        picklistValues: [],
        sourceUnit: null,
        settings: null
      } as SchemaField;

      const stepValueGetter = this.getRuleStepNameRenderer;
      cellRenderer = getCellRenderer(FieldDataType.Text);
      columns.push({
        headerName: sourceStep.displayName,
        field: sourceStep.systemName,
        colId: '_sourceStep',
        sortable: false,
        resizable: true,
        width: 150,
        menuTabs: [],
        cellRenderer,
        cellRendererParams: {
          schemaField: sourceStep,
          picklistService: this.picklistService
        },
        stepValueGetter
      });
    }

    const workflowStatus = {
      dataType: FieldDataType.UId,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.WORKFLOW-STATUS'),
      systemName: 'workflowStepUId',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null
    } as SchemaField;

    valueGetter = getValueGetter(FieldDataType.UId);
    cellRenderer = getCellRenderer(FieldDataType.UId);
    columns.push({
      headerName: workflowStatus.displayName,
      field: workflowStatus.systemName,
      colId: 'workflowStepUId',
      sortable: true,
      resizable: true,
      width: 200,
      menuTabs: [],
      cellRenderer,
      cellRendererParams: {
        workflows: this.workflows,
        workflowFieldName: 'workflowUId',
        workflowStepFieldName: 'workflowStepUId',
        schemaField: workflowStatus,
        picklistService: this.picklistService
      },
      valueGetter
    });

    const sentOn = {
      dataType: FieldDataType.DateTime,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.UPDATED-ON'),
      systemName: 'updatedOn',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    valueGetter = getValueGetter(FieldDataType.DateTime);
    cellRenderer = getCellRenderer(FieldDataType.DateTime);
    columns.push({
      headerName: sentOn.displayName,
      field: sentOn.systemName,
      colId: 'updatedOn',
      sortable: true,
      resizable: true,
      width: 200,
      menuTabs: [],
      cellRenderer,
      cellRendererParams: {
        schemaField: sentOn,
        picklistService: this.picklistService
      },
      valueGetter
    });

    const notificationType = {
      dataType: FieldDataType.Picklist,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.TYPE'),
      systemName: 'notificationType',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: 'COMMUNICATIONS.COLUMNS.TYPE'
    } as SchemaField;

    valueGetter = this.getNotificationTypeValue;
    cellRenderer = 'enumTranslateCellComponent';
    columns.push({
      headerName: notificationType.displayName,
      field: notificationType.systemName,
      colId: 'notificationType',
      sortable: true,
      resizable: true,
      width: 200,
      menuTabs: [],
      cellRenderer,
      cellRendererParams: {
        schemaField: notificationType,
        picklistService: this.picklistService
      },
      valueGetter
    });

    const createdBy = {
      dataType: FieldDataType.User,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.CREATED-BY'),
      systemName: 'createdById',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    valueGetter = getValueGetter(FieldDataType.User);
    cellRenderer = getCellRenderer(FieldDataType.User);
    columns.push({
      headerName: createdBy.displayName,
      field: createdBy.systemName,
      colId: 'createdById',
      sortable: true,
      resizable: true,
      width: 200,
      menuTabs: [],
      cellRenderer,
      cellRendererParams: {
        schemaField: createdBy,
        nameFieldName: 'createdByName',
        picklistService: this.picklistService
      },
      valueGetter
    });

    const sentBy = {
      dataType: FieldDataType.User,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.UPDATED-BY'),
      systemName: 'lastEditedById',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    valueGetter = getValueGetter(FieldDataType.User);
    cellRenderer = getCellRenderer(FieldDataType.User);
    columns.push({
      headerName: sentBy.displayName,
      field: sentBy.systemName,
      colId: 'lastEditedById',
      sortable: true,
      resizable: true,
      width: 200,
      menuTabs: [],
      cellRenderer,
      cellRendererParams: {
        schemaField: sentBy,
        nameFieldName: 'lastEditedByName',
        picklistService: this.picklistService
      },
      valueGetter
    });

    const recipients = {
      dataType: FieldDataType.Number,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.NUMBER-OF-RECIPIENTS'),
      systemName: 'recipientsCount',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: {
        format: 'NUMBER'
      }
    } as SchemaField;

    valueGetter = getValueGetter(FieldDataType.Number);
    cellRenderer = getCellRenderer(FieldDataType.Number);
    columns.push({
      headerName: recipients.displayName,
      field: recipients.systemName,
      colId: 'recipientsCount',
      sortable: false,
      resizable: true,
      width: 200,
      menuTabs: [],
      cellRenderer,
      cellRendererParams: {
        schemaField: recipients,
        picklistService: this.picklistService
      },
      valueGetter
    });

    return columns;
  }

  ngOnDestroy(): void {
    this.gridHelper.clearSearchFilter();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getRuleDatasetNameRenderer = (params: ValueGetterParams) => {
    return params.data.workflowState?.ruleDatasetName;
  };

  private getRuleStepNameRenderer = (params: ValueGetterParams) => {
    return params.data.workflowState?.ruleStepName;
  };

  private getNotificationActions(notification: MassNotification): any[] {
    const actions = [];
    const inReviewMode = this.isInReviewMode(notification);

    if (this.userContext?.hasPermission(Permissions.CommunicationNotificationUpdate)) {
      actions.push({
        label:
          inReviewMode || !this.userContext?.hasPermission(Permissions.CommunicationNotificationCreate)
            ? this.translateService.instant('COMMUNICATIONS.BUTTONS.REVIEW')
            : this.translateService.instant('COMMUNICATIONS.BUTTONS.EDIT'),
        icon: inReviewMode ? 'icon-dashboard' : 'icon-edit',
        click: data => {
          this.editClick(data);
        }
      });
    }

    if (!inReviewMode && this.userContext?.hasPermission(Permissions.CommunicationNotificationDelete)) {
      actions.push({
        label: this.translateService.instant('COMMUNICATIONS.BUTTONS.DELETE'),
        icon: 'icon-delete_trash',
        click: data => {
          this.deleteClick(data);
        }
      });
    }

    if (
      this.userContext?.hasPermission(Permissions.CommunicationNotificationRead) &&
      this.userContext?.hasPermission(Permissions.DocumentRead)
    ) {
      actions.push({
        label: this.translateService.instant('COMMUNICATIONS.BUTTONS.EXCEPTION-LOG'),
        icon: 'icon-history',
        click: data => {
          this.showLogsClick(data);
        }
      });
    }

    return actions;
  }

  newNotification() {
    this.store.dispatch(fromComm.newMassNotification());
  }

  navigate(path: string) {
    this.store.dispatch(
      fromRouting.go({
        routeData: { path: [path] }
      })
    );
  }

  editClick(notification: MassNotification) {
    if (this.isInReviewMode(notification)) {
      this.store.dispatch(fromComm.reviewMassNotification({ notification }));
    } else {
      this.store.dispatch(fromComm.editMassNotification({ notification }));
    }
  }

  showLogsClick(notification: MassNotification) {
    this.logNotification = notification;
  }

  onLogClosed() {
    this.logNotification = null;
  }

  private isInReviewMode(notification: MassNotification): boolean {
    const workflow = this.workflows?.find(w => w.uId === notification.workflowUId);
    const workflowStep = workflow?.workflowSteps?.find(w => w.uId === notification.workflowStepUId);

    if (
      notification.state === MassNotificationItemState.Ready ||
      workflowStep?.workflowStepType === WorkflowStepTypes.Send ||
      this.notificationCounts?.find(c => c.uId === notification.uId)?.count > 0 ||
      !!(notification as any).recipientsCount
    ) {
      return true;
    }

    return false;
  }

  deleteClick(notification: MassNotification) {
    this.notificationToDelete = notification;
  }

  goToStepClick(notification: MassNotification, destinationStepUId: string) {
    const updatedNotification = {
      ...notification,
      workflowStepUId: destinationStepUId
    };

    this.store.dispatch(fromComm.updateMassNotification({ notification: updatedNotification, close: true }));
  }

  confirmDeleteNotification(response: Boolean) {
    if (response) {
      this.store.dispatch(fromComm.deleteMassNotification({ notification: this.notificationToDelete }));
      this.dataTable.refreshGrid();
    }
    this.notificationToDelete = null;
  }

  getRecipientsValue = (params: ValueGetterParams) => {
    const result = params?.data?.recipients?.length;
    return result !== undefined ? result : 0;
  };

  getNotificationTypeValue = (params: ValueGetterParams) => {
    const field = params.colDef.cellRendererParams.schemaField;

    const reader = new DocumentReader(params.data);
    const value = reader.getFieldValue(field.systemName);

    return this.translateService.instant(`COMMUNICATIONS.NOTIFICATION-TYPE-${value}`);
  };

  getNotificationStateValue = (params: ValueGetterParams) => {
    const field = params.colDef.cellRendererParams.schemaField;

    const reader = new DocumentReader(params.data);
    const value = reader.getFieldValue(field.systemName);

    return this.translateService.instant(`COMMUNICATIONS.STATE-TYPE-${value}`);
  };

  private loadWorkflowTemplates(): void {
    this.getWorkflowTemplates()
      .pipe(take(1))
      .subscribe(data => {
        this.workflows = data;
        if (!!this.workflows) {
          this.columDefs = this.generateColumns();
        }
      });
  }

  private getWorkflowTemplates(): Observable<Workflow[]> {
    const params: QueryParams = {
      skip: 0,
      take: 1000,
      where: `IsActive=true && (ParentType=${WorkflowParentType.MassNotification})`,
      sortField: 'Name',
      sortDirection: 'Ascending'
    };
    return this.workflowApiService.getWorkflows(params, true).pipe(
      catchError(() => of({ data: [] })),
      map(({ data }) => {
        return data;
      })
    );
  }

  onGridDataLoaded(): void {
    this.isTableLoading = false;
  }

  getRowId = (params: GetRowIdParams): string => {
    return params.data.uId;
  };
}
